'use client';

import { A11Y_FILTER_DESCRIBED_BY } from '../../SearchResultsFacets/constants';

import { StyledRowContainer } from '@/components/SearchResultsFacets/styles';
import { checkRefinementApplied } from '@/components/SearchResultsFacets/utils';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    FILTER,
    FILTER_REMOVE,
    FILTER_TYPE_PRICE,
    FILTER_VAL,
    TRACK_TYPE_MULTI_ATTR,
} from '@/src/components/Analytics/constants';
import { FacetNormalized } from '@/src/components/SearchResultsFacets/types';
import useRefinement from '@/src/hooks/useRefinement';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Checkbox, List } from '@nxt-costco-com/forge-components';
import { useQueryParams } from '@nxt-costco-com/forge-digital-components';
import {
    Analytics,
    type ContentStackEntryDataProps,
} from '@nxt-costco-com/forge-digital-components';

export function PricingFilterView({
    facet,
    resultsText,
    facetConfig,
    searchService,
}: {
    facet: FacetNormalized;
    resultsText: string;
    facetConfig: ContentStackEntryDataProps;
    searchService: SearchServiceProps;
}) {
    const { handleDoRefine } = useRefinement({ searchService, facetConfig });
    const { queryParams: searchParams } = useQueryParams();

    const showMoreText = getKeyValue('facet.showmoretext', facetConfig);
    return (
        <List
            variant="ExpandableList"
            showMoreText={showMoreText}
            ariaLabeledById={facet.queryParamKey || facet.facetKey}
            buttonId={`show_more_${facet.label.toLowerCase().split(' ').join('_')}`}
        >
            {facet.buckets.map(bucket => {
                const isChecked = checkRefinementApplied({
                    facet,
                    bucket,
                    searchParams,
                    searchService,
                    facetConfig,
                });

                return (
                    <StyledRowContainer
                        key={bucket.val.toString()}
                        data-private={bucket.leads_to_fsa_chdi}
                        data-bi-private={bucket.leads_to_fsa_chdi}
                    >
                        <Analytics
                            analyticData={{
                                trackedData: [
                                    {
                                        key: isChecked ? FILTER_REMOVE : FILTER,
                                        value: FILTER_TYPE_PRICE,
                                    },
                                    {
                                        key: FILTER_VAL,
                                        value: bucket.val.toString(),
                                    },
                                ],
                                trackType: TRACK_TYPE_MULTI_ATTR,
                                doNotTrack: bucket.leads_to_fsa_chdi,
                            }}
                        >
                            <Checkbox
                                ariaDescribeText={A11Y_FILTER_DESCRIBED_BY}
                                onChange={event =>
                                    handleDoRefine(
                                        facet,
                                        bucket,
                                        event.target.checked,
                                    )
                                }
                                label={
                                    (
                                        <>
                                            <Box sx={visuallyHidden}>
                                                {`${facet.label}`}
                                            </Box>
                                            {bucket.val.toString()}{' '}
                                            <Box sx={visuallyHidden}>
                                                {`${bucket.count.toLocaleString()} ${resultsText}`}
                                            </Box>
                                        </>
                                    ) as unknown as string
                                }
                                isChecked={!!isChecked}
                                id={`${facet.facetKey}-${bucket.val.toString().split(' ').join('+')}`}
                            />
                        </Analytics>
                        <Box
                            id={`${facet.facetKey}-${bucket.val.toString().split(' ').join('+')}`}
                            aria-hidden
                        >
                            ({bucket.count.toLocaleString()})
                        </Box>
                    </StyledRowContainer>
                );
            })}
        </List>
    );
}
